import { AlertTriangle, MemoryStick, RotateCcw, Server } from 'lucide-react';
import { Card, CardBody } from '../ui/Card';
import { cn } from '../../utils/cn';
import type { HostStatsSummary } from '../../types';

function formatBytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(1)}GB` : `${(bytes / 1024 ** 2).toFixed(0)}MB`;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function UsageBar({ label, percent, detail }: { label: string; percent: number; detail: string }) {
  const tone = percent > 90 ? 'bg-rose-500' : percent > 80 ? 'bg-amber-500' : 'bg-pulse-500';
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-slate-500 dark:text-slate-400">{label}</span>
        <span className="font-semibold text-slate-700 dark:text-slate-300">{percent}%</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className={cn('h-full rounded-full transition-all', tone)} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">{detail}</p>
    </div>
  );
}

export function ServerHealthCard({ stats }: { stats: HostStatsSummary }) {
  return (
    <Card>
      <CardBody className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-pulse-50 text-pulse-600 dark:bg-pulse-900/30 dark:text-pulse-300">
              <Server size={16} />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{stats.hostname}</p>
              <p className="text-xs text-slate-400">{stats.osVersion}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span>Up {formatUptime(stats.uptimeSeconds)}</span>
            {stats.rebootRequired && (
              <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                <RotateCcw size={11} /> Reboot needed
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <UsageBar
            label="CPU load"
            percent={Math.min(Math.round((stats.cpuLoad1m / stats.cpuCount) * 100), 999)}
            detail={`${stats.cpuLoad1m.toFixed(2)} avg across ${stats.cpuCount} core(s)`}
          />
          <UsageBar
            label="RAM"
            percent={stats.memUsedPercent}
            detail={`${formatBytes(stats.memTotalBytes - stats.memAvailableBytes)} / ${formatBytes(stats.memTotalBytes)}`}
          />
          <UsageBar
            label="Disk"
            percent={stats.disk.usedPercent}
            detail={`${formatBytes(stats.disk.totalBytes - stats.disk.freeBytes)} / ${formatBytes(stats.disk.totalBytes)}`}
          />
        </div>

        {stats.swapTotalBytes > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-slate-400">
            <MemoryStick size={12} /> Swap: {formatBytes(stats.swapUsedBytes)} / {formatBytes(stats.swapTotalBytes)} used
          </p>
        )}
      </CardBody>
    </Card>
  );
}

export function ServerHealthUnavailable() {
  return (
    <Card>
      <CardBody className="flex items-center gap-3 text-sm text-slate-400">
        <AlertTriangle size={16} className="shrink-0 text-slate-300 dark:text-slate-600" />
        Server metrics need Linux <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">/proc</code> access -
        unavailable in this environment (expected on local Windows/Mac dev, works once deployed to the Ubuntu host).
      </CardBody>
    </Card>
  );
}
